import { clsx } from 'clsx';
import type { ReactNode } from 'react';

export interface BreadcrumbItem {
  label: string;
  href?: string;
}

/**
 * Breadcrumbs render the resource hierarchy, not the URL. In a control plane the
 * two diverge — `/developers/api-keys` is "Developers → API Keys" — and following
 * the URL would produce trails that read like file paths.
 */
export function Breadcrumbs({
  items,
  linkComponent: Link = 'a',
  className,
}: {
  items: readonly BreadcrumbItem[];
  linkComponent?: React.ElementType;
  className?: string;
}) {
  return (
    <nav aria-label="Breadcrumb" className={className}>
      <ol className="flex flex-wrap items-center gap-1 text-[12px]">
        {items.map((item, index) => {
          const isLast = index === items.length - 1;
          return (
            <li key={`${item.label}-${index}`} className="flex items-center gap-1">
              {item.href && !isLast ? (
                <Link
                  to={item.href}
                  href={item.href}
                  className={clsx(
                    'rounded-[var(--vc-radius-sm)] text-[var(--vc-text-muted)]',
                    'transition-colors duration-[var(--vc-duration-fast)] hover:text-[var(--vc-text)]',
                  )}
                >
                  {item.label}
                </Link>
              ) : (
                <span
                  className={
                    isLast ? 'font-medium text-[var(--vc-text)]' : 'text-[var(--vc-text-muted)]'
                  }
                  aria-current={isLast ? 'page' : undefined}
                >
                  {item.label}
                </span>
              )}
              {!isLast ? (
                <span aria-hidden="true" className="px-0.5 text-[var(--vc-text-subtle)]">
                  /
                </span>
              ) : null}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

export interface PageHeaderProps {
  title: string;
  description?: string;
  breadcrumbs?: readonly BreadcrumbItem[];
  linkComponent?: React.ElementType;
  actions?: ReactNode;
  /** Chips rendered beside the title — status, environment, resource id. */
  meta?: ReactNode;
  className?: string;
}

export function PageHeader({
  title,
  description,
  breadcrumbs,
  linkComponent,
  actions,
  meta,
  className,
}: PageHeaderProps) {
  return (
    <header className={clsx('min-w-0', className)}>
      {breadcrumbs && breadcrumbs.length > 0 ? (
        <Breadcrumbs items={breadcrumbs} linkComponent={linkComponent} className="mb-2.5" />
      ) : null}

      <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2.5">
            <h1 className="text-[19px] leading-tight font-semibold tracking-[-0.015em] text-[var(--vc-text)]">
              {title}
            </h1>
            {meta}
          </div>
          {description ? (
            <p className="mt-1.5 max-w-[78ch] text-[13px] leading-relaxed text-[var(--vc-text-muted)]">
              {description}
            </p>
          ) : null}
        </div>

        {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
      </div>
    </header>
  );
}
